import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useTour } from './Tour';

/**
 * What this hotel still has to do, worked out from its own data.
 *
 * Nothing here is ticked by hand. Every line is a query against what actually
 * exists, so a list that says the menu is priced is a list you can believe -
 * and one that un-ticks itself if somebody deletes the prices again.
 *
 * Hides itself entirely once everything is done rather than sitting there
 * saying "100%", which is a panel nobody ever reads twice.
 */

interface ChecklistStep {
  id: string;
  label: string;
  hint: string;
  done: boolean;
  route: string;
}

interface Checklist {
  steps: ChecklistStep[];
  done: number;
  total: number;
  percent: number;
}

export function GettingStarted() {
  const { available, start } = useTour();
  const checklist = useQuery({
    queryKey: ['onboarding', 'checklist'],
    queryFn: () => api.get<Checklist>('/api/onboarding/checklist'),
    // It changes as they work, and it is cheap; a stale tick is worse than a
    // refetch on the screen they land back on.
    staleTime: 30_000,
  });

  const data = checklist.data;
  if (!data || data.total === 0 || data.done === data.total) return null;

  return (
    <div className="card getting-started" data-tour="checklist">
      <div className="card-head">
        <div>
          <h2>Your setup is {data.percent}% complete</h2>
          <div className="small muted">
            {data.total - data.done} thing{data.total - data.done === 1 ? '' : 's'} left before this
            hotel is running on its own.
          </div>
        </div>
        {available ? (
          <button className="btn sm" onClick={start}>
            Take the tour
          </button>
        ) : null}
      </div>

      <div className="card-body col gap-12">
        <div className="setup-meter" aria-hidden="true">
          <div className="setup-meter-fill" style={{ width: `${data.percent}%` }} />
        </div>

        <ul className="setup-list">
          {data.steps.map((step) => (
            <li key={step.id} className={step.done ? 'done' : undefined}>
              <span className="setup-mark" aria-hidden="true">
                {step.done ? '✓' : '○'}
              </span>
              <span className="grow">
                {step.done ? (
                  <span className="setup-label">{step.label}</span>
                ) : (
                  <Link className="setup-label" to={step.route}>
                    {step.label}
                  </Link>
                )}
                {!step.done ? <span className="xs muted setup-hint">{step.hint}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
